<template>
  <fin-dialog ref="refListDialog"
              v-resize="onResize"
              :dense="$vuetify.breakpoint.smAndDown"
              :fullscreen="fullScreen"
              :max-width="maxWidth"
              :value="value"
              content-class="fin-change-dialog"
              persistent
              @input="e => resetDialog(e)">

    <template #title>
      <template v-if="!editMode">
        <span :class="$vuetify.breakpoint.smAndDown ? 'px-3 list-title' : 'list-title'">
          {{ dialogTitle }}
          <span v-if="!onlyHistory"> bearbeiten</span>
        </span>
      </template>
      <template v-else>
        <span class="px-3 list-title" style="display: inline-flex;">
          <v-tooltip top>
            <template #activator="{ on }">
              <v-icon v-if="selectedTransaction.transaction === 'SELL'"
                      color="fin-color-primary-dark"
                      class="mr-1"
                      v-on="on">mdi-playlist-minus</v-icon>
              <v-icon v-if="selectedTransaction.transaction === 'BUY'"
                      color="fin-color-primary-dark"
                      class="mr-1"
                      v-on="on">mdi-playlist-plus</v-icon>
              <v-icon v-if="selectedTransaction.transaction === 'CASH_PAY_IN'"
                      color="fin-color-primary-dark"
                      class="mr-1"
                      v-on="on">mdi-bank-plus</v-icon>
              <v-icon v-if="selectedTransaction.transaction === 'CASH_PAY_OUT'"
                      color="fin-color-primary-dark"
                      class="mr-1"
                      v-on="on">mdi-bank-minus</v-icon>
              <v-icon v-if="selectedTransaction.transaction === 'DIVIDEND'"
                      color="fin-color-primary-dark"
                      class="mr-1"
                      v-on="on">mdi-sack-percent</v-icon>
            </template>
            <span v-if="selectedTransaction.transaction === 'SELL'">Verkauf</span>
            <span v-if="selectedTransaction.transaction === 'BUY'">Kauf</span>
            <span v-if="selectedTransaction.transaction === 'CASH_PAY_IN'">Einzahlung</span>
            <span v-if="selectedTransaction.transaction === 'CASH_PAY_OUT'">Auszahlung</span>
            <span v-if="selectedTransaction.transaction === 'DIVIDEND'">Dividende</span>
          </v-tooltip>

          <span>{{ dynDialogTitle }} {{ createNew ? 'erstellen' : 'bearbeiten' }}</span>
        </span>
      </template>
    </template>

    <fin-data-loader ref="transactionsLoader" :resolver="loadTransactions">
      <template #content>
        <template v-if="!editMode">
          <v-card-text :class="$vuetify.breakpoint.smAndDown ? 'pa-3 list-card' : 'pa-0 list-card'"
                       style="position: relative;">
            <v-row class="my-0" ref="dropdownFilters">
              <v-col cols="12" style="line-height: 24px;" v-if="!onlyHistory">Wähle den Eintrag, den du ändern oder
                löschen möchtest.
              </v-col>
              <v-col cols="12">
                <fin-dropdown v-if="instruments.length !== 1"
                              v-model="activeInstrumentFilter"
                              :dropdown-height="dropdownMaxHeight"
                              :items="uniqueInstruments"
                              :show-selection="$vuetify.breakpoint.mdAndUp"
                              :show-selection-badge="$vuetify.breakpoint.smAndDown"
                              attach=".list-card"
                              class="filter-dropdown"
                              label="Wertpapier"
                              output-label-attribute="name"
                              single-select/>
                <!-- under this width the dropdowns do not fit on one line -->
                <br v-if="windowInnerWidth < 400 && instruments.length !== 1"/>
                <fin-dropdown v-if="activeFilter"
                              v-model="activeFilter"
                              :dropdown-height="dropdownMaxHeight"
                              :items="availableFilters"
                              :show-selection="$vuetify.breakpoint.mdAndUp"
                              :show-selection-badge="$vuetify.breakpoint.smAndDown"
                              attach=".list-card"
                              class="filter-dropdown"
                              label="Ansicht"
                              single-select/>
              </v-col>
            </v-row>

            <div v-if="activeFilter.length !== 0 && activeFilter[0].id === 'DIVIDENDS'"
                 class="my-5"
                 style="text-align: right;">
              <v-btn text v-if="!isAutoDividendenServiceEnabled"
                     @click="showDividendenServiceAboUpgradeDialog">
                Dividendenservice
              </v-btn>
              <v-btn :disabled="disableDividendButton"
                     @click="showDividendForm">
                <v-icon>icon-plus</v-icon>
                Dividende erfassen
              </v-btn>
            </div>

            <fin-data-table :headers="tableHeaders"
                            :item-class="highlightLastEditedRow"
                            :resolver="transactionsModel"
                            :stickyLastCol="!onlyHistory"/>
          </v-card-text>
        </template>

        <template v-else>
          <fin-edit-transaction-form v-model="selectedTransaction"
                                     @cancel="resetEditForm"
                                     @updatePortfolio="val => updatePortfolio(val)"
                                     :create-new="createNew"
                                     :first-buy-date="firstBuyDate(selectedTransaction)"
                                     :first-sell-date="firstSellDate(selectedTransaction)"
                                     :is-oldest-entry="isOldestEntry"/>
        </template>
      </template>
    </fin-data-loader>
  </fin-dialog>
</template>

<script>
import FinDataTable from "@/components/common/DataTable/FinDataTable"
import {TablePresets as DefaultHeaders} from "@/components/common/DataTable/DefaultHeaders"
import FinEditTransactionForm from "@/components/common/transactions/EditTransactionForm"
import {finPortfolioAPI} from "@/js/finAPI"
import FinDropdown from "@/components/common/Dropdown"
import AmplitudePush from "@/mixins/AmplitudePush"
import FinDataLoader from "@/components/common/DataLoader"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {getInstrumentsByFilter} from "@/components/common/transactions/ListDialog/graphql"
import DataFetcher from "@/components/common/DataLoader/DataFetcher"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import {EventBus} from "@/plugins/fincraft"
import PriceModel from "@/mixins/PriceModel"

const FILTERS = [{
  id: 'DEFAULT',
  title: 'Standard'
}, {
  id: 'ALL',
  title: 'Alle',
  disabledOnMobiles: true
}, {
  id: 'BUYSELL',
  title: 'Käufe/Verkäufe',
  typeFilters: ['SELL', 'BUY'],
  disabledOnMobiles: true
}, {
  id: 'CASHES',
  title: 'Ein-/Auszahlungen',
  typeFilters: ['CASH_PAY_IN', 'CASH_PAY_OUT'],
  hideOnInstrumentView: true,
},/* {
  id: 'SUCCESS',
  title: 'Gesamterfolg',
  typeFilters: ['BUY', 'SELL', 'DIVIDEND'],
}, {
  id: 'SUCCESS_OPEN',
  title: 'Erfolg offene Pos.',
  typeFilters: ['BUY'],
  statusFilters: ['OPEN', 'PARTIALLY_SOLD']
}, {
  id: 'SUCCESS_SOLD',
  title: 'Erfolg verk. Pos.',
  typeFilters: ['BUY'],
  statusFilters: ['SOLD']
}, */ {
  id: 'SELLS',
  title: 'Verkäufe',
  typeFilters: ['SELL'],
}, {
  id: 'BUYS',
  title: 'Käufe',
  typeFilters: ['BUY'],
}, {
  id: 'DIVIDENDS',
  title: 'Dividenden',
  typeFilters: ['DIVIDEND']
}, {
  id: 'SPLITS',
  title: 'Splits',
  typeFilters: ['SPLIT'],
},]

export default {
  name: "FinTransactionsListDialog",
  components: {FinDataLoader, FinDropdown, FinEditTransactionForm, FinDataTable},
  props: {
    /** Determines if the dialog is visible or not */
    value: {
      type: Boolean,
      default: false
    },
    /** The max width of the dialog */
    maxWidth: {
      type: Number,
      default: 1000
    },
    /** Sets if the dialog should be displayed in fullscreen mode */
    fullScreen: {
      type: Boolean,
      default: false
    },
    /** Sets the title of the dialog */
    dialogTitle: {
      type: String,
      default: 'Transaktionen'
    },
    /** Sets the selected view id (uppercase), otherwise use ALL */
    selectedViewId: {
      type: String,
      default: 'DEFAULT'
    },
    /** The instruments */
    instruments: {
      type: Array,
      default: () => []
    },
    /** The ID of the currently selected portfolio */
    portfolioId: {
      type: String,
    },
    /** If set the context menu of the fin-data-table actions col is hidden */
    onlyHistory: {
      type: Boolean,
      default: false
    },
    /** Custom transaction resolver, used e.g. for storybook / jest */
    transactionResolver: {},
    /** Show global transactions or instrument's transactions */
    globalTransactions: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      transactions: [],
      transactionsModel: [],
      editMode: false,
      selectedTransaction: undefined,
      activeFilter: [],
      dynDialogTitle: this.dialogTitle,
      createNew: false,
      activeInstrumentFilter: [{id: 'ALL', name: 'Alle'}],
      lastEditedTransactionTimeStamp: undefined,
      isOldestEntry: false,
      windowInnerWidth: window.innerWidth,
      dropdownMaxHeight: '50vh'
    }
  },
  computed: {
    availableFilters() {
      let filters = this.$vuetify.breakpoint.xs ? FILTERS.filter(f => f.disabledOnMobiles !== true) : FILTERS
      return !this.globalTransactions ? filters.filter(f => !f.hideOnInstrumentView) : filters
    },
    uniqueInstruments() {
      const uniqueInstr = [...new Map(this.transactions.map((item) => [item["instrumentId"], item])).values()]
      return [
        {
          id: 'ALL',
          name: 'Alle'
        },
        ...uniqueInstr.filter(i => i.instrumentId !== null)
          .map(i => i.instrument)
          .sort((a, b) => a.name.localeCompare(b.name))
      ]
    },
    tableHeaders() {
      let tableActions = []
      if (!this.onlyHistory) {
        tableActions.push({
          id: 'EDIT',
          name: 'Bearbeiten',
          disabled: (item) => {
            return item.transaction === 'SPLIT' || (item.instrument && (!item.instrument.active || !item.instrument.covered || item.instrument.delisted))
          },
          click: (item) => {
            this.editTransaction(item)
          }
        })
        tableActions.push(
          {
            id: 'DELETE',
            name: 'Löschen',
            disabled: (item) => {
              return item.transaction === 'SPLIT'
            },
            click: (item) => {
              this.confirmDeleteTransaction(item)
            },
          })
      }
      return DefaultHeaders.createTransactionListHeaders(tableActions, this.activeFilter.length === 0 ? FILTERS[0] : this.activeFilter[0], this.$vuetify.breakpoint, this.lastEditedTransactionTimeStamp)
    },
    disableDividendButton() {
      // edit single instrument -> enable dividend button
      if (!this.globalTransactions && this.instruments && this.instruments.length === 1) {
        return false
      }

      // history mode: check if one instrument but not ALL is selected
      return this.activeInstrumentFilter.length > 0 && this.activeInstrumentFilter[0].id === 'ALL'
    },
    isAutoDividendenServiceEnabled() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.AUTO_DIVIDEND_SERVICE.id)
    },
  },
  mixins: [AmplitudePush, PriceModel],
  methods: {
    showDividendenServiceAboUpgradeDialog() {
      EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.AUTO_DIVIDEND_SERVICE.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
    },
    onResize() {
      this.windowInnerWidth = window.innerWidth
    },
    async loadTransactions() {
      // load all transactions when this.instrumentId is not set,
      // load only the transactions concerning this instrument if set
      let transactionsItems
      let transactions
      let missingInstruments = []

      if (this.transactionResolver) {
        transactions = await DataFetcher.fetchData(this.transactionResolver)
      } else {
        transactionsItems = await finPortfolioAPI.listPortfolioTransactions(this.portfolioId, this.globalTransactions ? null : this.instruments[0].id)

        if (transactionsItems) {
          transactions = transactionsItems?.items

          if (transactions) {
            // the instruments which were completely sold are not contained in the props instruments. We therefore need to get them.
            const missingInstrumentIds = new Set(transactions.map(item => {
              if (item.instrumentId &&
                this.instruments.find(instr => instr.instrument && instr.instrument.id === item.instrumentId)?.instrument === undefined) {
                return item.instrumentId
              }
            }).filter(item => item !== undefined))
            // get instruments for all instrumentIds
            missingInstruments = await this.getInstrumentsByIds(missingInstrumentIds)
          }
        }
      }

      // mapping each transaction to its instrument
      transactions = transactions.map(item => {
        let instrument
        if (!this.globalTransactions) {
          instrument = this.instruments[0]
        } else {
          instrument = this.instruments.find(instr => instr.instrument && instr.instrument.id === item.instrumentId)?.instrument
          if (!instrument) {
            instrument = missingInstruments.find(instr => instr.id === item.instrumentId)
          }
        }
        return {
          ...item,
          instrument: instrument
        }
      })

      this.transactions = transactions
    },
    // the instruments which were completely sold are not contained in the props instruments. We therefore need to get them.
    async getInstrumentsByIds(instrumentsIds) {
      let searchOrArray = []
      instrumentsIds.forEach(item => {
        if (item) {
          searchOrArray.push({id: {eq: item}})
        }
      })

      try {
        const result = await API.graphql(graphqlOperation(getInstrumentsByFilter, {
          filter: {
            or: searchOrArray
          }
        }))

        if (result) {
          return result.data.searchInstruments.items.map(item => item.instrument)
        }
      } catch (err) {
        console.error("failed to get instruments for isins " + instrumentsIds, err)
      }
    },
    firstBuyDate(selectedTransaction) {
      const allBuys = this.transactions.filter(transaction => transaction.transaction === 'BUY' && transaction.instrument?.id === selectedTransaction.instrument?.id)
      const firstBuy = allBuys && allBuys.length > 0 ? allBuys[allBuys.length - 1] : null
      if (!firstBuy) {
        return undefined
      } else if (firstBuy.realizedTimestamp === null) {
        return new Date(firstBuy.actionTimestamp)
      } else {
        return new Date(firstBuy.realizedTimestamp)
      }
    },
    firstSellDate(selectedTransaction) {
      const allSells = this.transactions.filter(transaction => transaction.transaction === 'SELL' && transaction.instrument?.id === selectedTransaction.instrument?.id)
      const firstSell = allSells && allSells.length > 0 ? allSells[allSells.length - 1] : null
      if (!firstSell) {
        return undefined
      } else if (firstSell.realizedTimestamp === null) {
        return new Date(firstSell.actionTimestamp)
      } else
        return new Date(firstSell.realizedTimestamp)
    },
    setDropdownHeight() {
      const dialogHeight = this.$refs.refListDialog?.getScrollElement()?.clientHeight - (this.$vuetify.breakpoint.smAndDown ? 12 : 44) // 12/ 64 but only use 44 = padding
      // set fallback value to 68px = single line title height
      const title = document.querySelector('.fin-dialog-title')?.getBoundingClientRect() | 68
      const dropdownRect = this.$refs.dropdownFilters?.getClientRects()[0]
      const dropdownFiltersRowHeight = dropdownRect?.height | 0

      // either access title object or use title value instead
      const res = dialogHeight - dropdownFiltersRowHeight - (title?.height || title)
      if (res > 0) {
        this.dropdownMaxHeight = res + 'px'
      }
    },
    highlightLastEditedRow(item) {
      if (this.lastEditedTransactionTimeStamp && this.lastEditedTransactionTimeStamp === item.actionTimestamp) {
        return 'highlighted-row'
      }
    },
    trackToAmplitude(action) {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_instruments_table_transactions_dialog_' + action,
        'step': action,
        'placement': 'portfolio_details_instruments_table',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    showDividendForm() {
      this.editMode = true
      this.dynDialogTitle = 'Dividende'
      this.createNew = true
      this.selectedTransaction = {
        transaction: 'DIVIDEND',
        instrument: !this.globalTransactions ? this.instruments[0] : {id: this.activeInstrumentFilter[0].id}, // for creating dividends only the instrument.id is used
        portfolioId: this.portfolioId,
        actionTimestamp: new Date(),
        dps: 0
      }
      this.trackToAmplitude('create_dividend')
    },
    resetEditForm() {
      this.editMode = false
      this.createNew = false
      this.selectedTransaction = null
    },
    resetDialog(e) {
      if (this.editMode) {
        this.editMode = false
      } else {
        this.editMode = false
        this.createNew = false
        this.selectedTransaction = null
        this.dynDialogTitle = 'Transaktionen'
        this.activeFilter = this.availableFilters.filter(f => f.id === this.selectedViewId)
        this.$emit('input', e)
      }
    },
    editTransaction(transaction) {
      this.selectedTransaction = transaction

      if (transaction.transaction === 'BUY') {
        this.dynDialogTitle = 'Kauf (' + transaction.instrument.name + ')'
      } else if (transaction.transaction === 'SELL') {
        this.dynDialogTitle = 'Verkauf (' + transaction.instrument.name + ')'
      } else if (transaction.transaction === 'CASH_PAY_IN') {
        this.dynDialogTitle = 'Einzahlung'
      } else if (transaction.transaction === 'CASH_PAY_OUT') {
        this.dynDialogTitle = 'Auszahlung'
      } else if (transaction.transaction === 'DIVIDEND') {
        this.dynDialogTitle = 'Dividende (' + transaction.instrument.name + ')'
      }

      this.isOldestEntry = this.transactionsModel.findIndex(transaction => transaction.createdAt === this.selectedTransaction.createdAt) === this.transactionsModel.length - 1
      this.editMode = true
      this.trackToAmplitude('edit_transaction_' + transaction.transaction.toLowerCase())
    },
    confirmDeleteTransaction(transaction) {
      this.$confirm({
        title: 'Transaktion löschen?',
        text: 'Möchtest Du deine Transaktion löschen?',
        textWithLexikonLink: false,
        confirmBtn: 'Ok, löschen'
      })
        .then(() => {
          this.deleteTransaction(transaction)
          this.trackToAmplitude('delete_transaction_' + transaction.transaction.toLowerCase())
        })
    },
    async deleteTransaction(transaction) {
      try {
        const resp = await finPortfolioAPI.transactionDelete(transaction.portfolioId, transaction.actionTimestamp,
          transaction.transaction)

        if (resp.isValid) {
          this.$emit('updatePortfolio')
          this.$toasted.global.finToast({
            message: 'Deine Transaction wurde erfolgreich gelöscht.',
            type: 'success',
          })

          // remove transaction aus dem Dialog
          this.transactions = this.transactions.filter(item => item.actionTimestamp !== transaction.actionTimestamp &&
            item.instrumentId !== transaction.instrumentId)
        } else {
          //TODO translate messages?
          this.$toasted.global.finToast({
            message: 'Deine Transaction konnte nicht gelöscht werden. ' + resp.cause,
            type: 'error'
          })
        }

      } catch (e) {
        console.error("Failed to delete transaction", e)
        this.$toasted.global.finToast({
          message: 'Deine Transaction konnte nicht gelöscht werden.',
          type: 'error'
        })
      }
    },
    updatePortfolio(transaction) {
      this.selectedTransaction = undefined

      let idx = this.transactionsModel.findIndex(t =>
        t.portfolioId === transaction.portfolioId &&
        t.actionTimestamp === transaction.actionTimestamp &&
        t.transaction === transaction.transaction
      )
      if (idx !== -1) {
        this.transactionsModel[idx] = transaction
      }
      this.$refs.transactionsLoader._fetchData()
      this.editMode = false
      this.createNew = false
      this.isOldestEntry = false
      this.lastEditedTransactionTimeStamp = transaction.actionTimestamp
      this.$emit('updatePortfolio')
    },
    doTransactionFiltering() {
      let filteredTransactions = this.transactions

      if (filteredTransactions.length > 0 && this.activeFilter.length !== 0) {
        if (this.activeFilter[0].typeFilters) {
          filteredTransactions = filteredTransactions.filter(t => this.activeFilter[0].typeFilters.includes(t.transaction))
        }

        if (filteredTransactions && this.activeFilter[0].statusFilters) {
          filteredTransactions = filteredTransactions.filter(ft => this.activeFilter[0].statusFilters.includes(ft.sellStatus))
        }
      }

      if (filteredTransactions.length > 0 && this.activeInstrumentFilter.length !== 0 && this.activeInstrumentFilter[0].id !== 'ALL') {
        filteredTransactions = filteredTransactions.filter(ft => {
          if (ft.instrument) {
            return ft.instrument.id === this.activeInstrumentFilter[0].id
          }
        })
      }

      this.transactionsModel = filteredTransactions

      this.$nextTick(() => setTimeout(this.setDropdownHeight, 100))
    }
  },
  watch: {
    transactions() {
      this.transactionsModel = this.transactions

      if ((this.activeFilter.length !== 0 && this.activeFilter[0].id !== 'ALL') ||
        (this.activeInstrumentFilter.length !== 0 && this.activeInstrumentFilter[0].id)) {
        this.doTransactionFiltering()
      }
      this.$nextTick(() => setTimeout(this.setDropdownHeight, 100))
    },
    activeFilter: {
      handler() {
        this.doTransactionFiltering()
      },
      immediate: true,
    },
    activeInstrumentFilter() {
      this.doTransactionFiltering()
    },
    selectedViewId: {
      handler() {
        this.activeFilter = this.selectedViewId ? [FILTERS.find(f => f.id === this.selectedViewId)] : [FILTERS[0]]
      },
      immediate: true,
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .fin-change-dialog {
  box-shadow: $fin-color-dialog-shadow !important;
}

::v-deep .highlighted-row {
  background-color: $fin-color-accent-beige
}
</style>
